/**
 * Calendar Utilities
 *
 * Helper functions for calendar views and post scheduling
 */

import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

/**
 * Generate the calendar day grid for a month view anchored to team timezone.
 *
 * Each returned Date represents noon in the team timezone, so that downstream
 * formatters using `formatInTimeZone(day, teamTimezone, ...)` always display
 * the intended calendar date regardless of the browser's local timezone.
 *
 * @param currentMonth - Any Date whose UTC value falls within the target month
 * @param teamTimezone - IANA timezone string (e.g. 'Europe/Madrid')
 * @returns Array of Date objects (one per grid cell, Mon-start weeks)
 */
export function generateCalendarDays(currentMonth: Date, teamTimezone: string): Date[] {
  const zonedMonth = toZonedTime(currentMonth, teamTimezone);
  const monthStart = startOfMonth(zonedMonth);
  const monthEnd = endOfMonth(zonedMonth);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const days: Date[] = [];
  let cursor = gridStart;

  while (cursor <= gridEnd) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, '0');
    const d = String(cursor.getDate()).padStart(2, '0');
    days.push(fromZonedTime(`${y}-${m}-${d}T12:00:00`, teamTimezone));
    cursor = addDays(cursor, 1);
  }

  return days;
}

/**
 * Return a Date anchored at noon in the team timezone for "today".
 * Comparing against this ensures isToday checks are team-tz–correct.
 *
 * @param teamTimezone - IANA timezone string
 */
export function todayInTimezone(teamTimezone: string): Date {
  const zonedNow = toZonedTime(new Date(), teamTimezone);
  const y = zonedNow.getFullYear();
  const m = String(zonedNow.getMonth() + 1).padStart(2, '0');
  const d = String(zonedNow.getDate()).padStart(2, '0');
  return fromZonedTime(`${y}-${m}-${d}T12:00:00`, teamTimezone);
}

/**
 * Minimal set of status-signal fields the status-inference helpers below
 * need. The full `Post` type is a structural superset and satisfies this
 * directly. Narrower objects that only carry an already-server-healed
 * `status` (e.g. `MediaLibraryItem['post']`, whose backend already resolves
 * FAILED/PUBLISHED via `inferPostStatus` before it reaches the frontend, and
 * never forwards raw `calendarMappings`/`postChannels`/`instancesSummary`)
 * also satisfy it, without needing the full `Post` shape.
 */
export interface PostStatusSignals {
  status: string;
  scheduledAt?: string | Date | null;
  instancesSummary?: {
    scheduled: number;
    published: number;
    failed: number;
    mapped?: number;
    // First-class occurrence buckets (Stage 2c-i) — un-folded from `failed`.
    publishing?: number;
    partial?: number;
    missed?: number;
    cancelled?: number;
  } | null;
  calendarMappings?: { status: string }[];
  postChannels?: { status?: string }[];
}

/**
 * Check if a post is unmapped (not scheduled)
 *
 * @param post - Post to check
 * @returns True if unmapped
 */
export function isPostUnmapped(post: PostStatusSignals): boolean {
  return !post.scheduledAt && post.status === 'UNSCHEDULED';
}

/**
 * Check if a post is published
 *
 * Unions across all four status signals a `Post` can carry depending on its
 * data source: the post-level `status` (raw list / most sources), the
 * `instancesSummary` counts (raw-list / scheduled-instance source),
 * `calendarMappings[].status` (composer / getById source, which doesn't
 * always include `instancesSummary`), and `postChannels[].status` (legacy
 * posts published only via post-channels, with no calendar mapping and a
 * stale `post.status`).
 *
 * PARTIAL/MISSED caveat (updated post-Stage-1 un-fold, see
 * docs/architecture/POST-STATUS-MODEL.md): `computeInstancesSummary` now
 * buckets Partial/Missed occurrences into their OWN first-class
 * `instancesSummary.partial`/`.missed` counters, separate from `.failed` and
 * `.published`. A purely-PARTIAL post therefore has `instancesSummary.published
 * === 0`, so this function relies on the `calendarMappings[].status ===
 * 'PUBLISHED'` branch to still call it "published" here (a partial occurrence
 * is persisted as `CalendarMapping.status='PUBLISHED'` — the enum has no
 * PARTIAL value). This is intentional: `isPostFailed` (below) also matches a
 * partial post via `instancesSummary.partial`, and per `getPrimaryStatus`'s
 * Failed-first check order, Failed wins the tie so a partial post never shows
 * as plain "published" through the combined helper. This function alone,
 * called in isolation, will still report `true` for a partial post — callers
 * needing an exclusive success/failure split should use `getPrimaryStatus`,
 * not this helper directly.
 *
 * @param post - Post to check
 * @returns True if published
 */
export function isPostPublished(post: PostStatusSignals): boolean {
  if (post.status === 'PUBLISHED') return true;
  if (post.instancesSummary && post.instancesSummary.published > 0) return true;
  if (post.calendarMappings?.some((m) => m.status === 'PUBLISHED')) return true;
  if (post.postChannels?.some((pc) => pc.status === 'PUBLISHED')) return true;
  return false;
}

/**
 * Check if a post has failed OR needs attention (Failed / Partial / Missed).
 *
 * Unions across `post.status`, `instancesSummary`, `calendarMappings[].status`,
 * and `postChannels[].status` (see `isPostPublished` for why all four signals
 * matter).
 *
 * PARTIAL/MISSED handling (updated post-Stage-1 un-fold — see
 * docs/architecture/POST-STATUS-MODEL.md "Counts / metrics semantics"):
 * `computeInstancesSummary` no longer folds Partial/Missed occurrences into
 * `instancesSummary.failed` — they land in their own first-class
 * `.partial`/`.missed` buckets. This function is a NEEDS-ATTENTION predicate
 * (mirroring the backend's `needsAttentionPostOr`/`failedPostOr`, which
 * deliberately fold Failed + Partial + Missed together as "needs a human to
 * look at it" — a genuinely different, valid aggregate intent from the
 * display fold), so it explicitly checks `instancesSummary.partial > 0` and
 * `instancesSummary.missed > 0` in addition to `.failed > 0`. Without this,
 * a purely-PARTIAL post (some channels published, some failed) would report
 * `false` here, because `calendarMappings[].status` stores that case as
 * `'PUBLISHED'` (the enum has no PARTIAL value) and `postChannels[].status`
 * may not carry a `FAILED` row either for a calendar-mapping-based publish.
 *
 * A data source that supplies `calendarMappings`/`postChannels` WITHOUT
 * `instancesSummary` still cannot detect Partial/Missed at all (no raw-mapping
 * fallback exists for those states) and will under-report for such posts.
 *
 * NOTE: this function backs `getPrimaryStatus`, the fallback single-badge
 * helper. Display surfaces should prefer `post.displayStatus` /
 * `resolveOccurrenceStatus` (which surface Partial/Missed AS THEMSELVES,
 * never folded into "Failed") — this helper's "failed" is a needs-attention
 * boolean, not a display label.
 *
 * @param post - Post to check
 * @returns True if failed, partial, or missed (needs attention)
 */
export function isPostFailed(post: PostStatusSignals): boolean {
  if (post.status === 'FAILED') return true;
  if (post.instancesSummary) {
    if (post.instancesSummary.failed > 0) return true;
    if ((post.instancesSummary.partial ?? 0) > 0) return true;
    if ((post.instancesSummary.missed ?? 0) > 0) return true;
  }
  if (post.calendarMappings?.some((m) => m.status === 'FAILED')) return true;
  if (post.postChannels?.some((pc) => pc.status === 'FAILED')) return true;
  return false;
}

/**
 * Check if a post is scheduled
 *
 * A genuinely published/failed post is NOT "scheduled" even if `post.status`
 * is a stale `'SCHEDULED'` (the scheduled-path desync bug). Defer to the
 * instance/mapping layer first via `isPostPublished`/`isPostFailed` before
 * trusting the post-level status.
 *
 * @param post - Post to check
 * @returns True if scheduled
 */
export function isPostScheduled(post: PostStatusSignals): boolean {
  if (isPostPublished(post) || isPostFailed(post)) return false;
  if (post.instancesSummary && post.instancesSummary.scheduled > 0) return true;
  if (post.calendarMappings?.some((m) => m.status === 'SCHEDULED')) return true;
  if (post.status === 'SCHEDULED') return true;
  return !!post.scheduledAt && post.status === 'UNSCHEDULED';
}

/**
 * Get primary status badge for a post
 * (Priority: Failed > Published > Scheduled > Unmapped)
 *
 * `isPostScheduled` returns false whenever `isPostPublished` or `isPostFailed`
 * is true, so Scheduled never conflicts with the other two. Failed and
 * Published, however, are NOT mutually exclusive: a genuinely PARTIAL post
 * (some channels published, some failed) makes both `isPostPublished` and
 * `isPostFailed` return true (`isPostFailed` explicitly treats Partial/Missed
 * as needs-attention — see its docstring). The check order below is what
 * resolves that tie — Failed is checked FIRST so it wins for partial posts.
 * Do NOT reorder these checks.
 *
 * @param post - Post to check
 * @returns Primary status
 *
 * NOTE (Stage 2c-i): the return type now includes 'partial' | 'missed' |
 * 'cancelled' | 'publishing' to make the full occurrence vocabulary
 * available to callers/components ahead of time. The collapse LOGIC below
 * is still Failed-first (folding partial/missed into the returned 'failed'
 * label, since `isPostFailed` folds them as needs-attention) — deciding
 * whether/how this helper should surface Partial/Missed as their OWN
 * returned label (vs. reading `post.displayStatus`, which already does) is
 * Stage 2c-ii's job. This function is the FALLBACK path only; prefer
 * `post.displayStatus` at call sites. See docs/architecture/POST-STATUS-MODEL.md.
 */
export function getPrimaryStatus(
  post: PostStatusSignals
): 'failed' | 'scheduled' | 'published' | 'unmapped' | 'draft' | 'partial' | 'missed' | 'cancelled' | 'publishing' {
  if (isPostFailed(post)) return 'failed';
  if (isPostPublished(post)) return 'published';
  if (isPostScheduled(post)) return 'scheduled';
  if (isPostUnmapped(post)) return 'unmapped';
  return 'draft';
}

export interface PostStatusCounts {
  failed: number;
  published: number;
  scheduled: number;
  mapped: number;
  // First-class occurrence buckets (Stage 2c-i) — select-not-sum from
  // `instancesSummary` when present; undefined when the data source doesn't
  // supply it (no reliable raw-mapping fallback exists for these states).
  partial?: number;
  missed?: number;
  cancelled?: number;
  publishing?: number;
}

/**
 * Aggregate calendar-mapping + instancesSummary counts for a POST (mirrors
 * `getVariationStatusCounts` for variations).
 *
 * `instancesSummary` (when the backend supplies it) is DERIVED server-side
 * from this SAME `calendarMappings` array that is also returned in the
 * payload — it is not an independent signal. Summing the raw mapping tally
 * with `instancesSummary` therefore double-counts every mapping (e.g. 2
 * published mappings would render as 4 published). FIX: prefer
 * `instancesSummary` when present (select, not sum), and only fall back to
 * the raw mapping tally when `instancesSummary` is absent (backend not yet
 * returning it, or a data source that never included it).
 *
 * `mapped` is SELECTED off `instancesSummary.mapped` when present (backend's
 * `computeInstancesSummary` derives it via `resolveOccurrenceStatus`, i.e.
 * from the live `ScheduledInstance`, not the raw mapping cache — this is
 * instance-consistent and un-stale), mirroring how the other buckets already
 * work. It falls back to the raw `calendarMappings` MAPPED tally only when
 * `instancesSummary` (or its `mapped` field) is absent. This avoids
 * double-counting an occurrence whose `CalendarMapping.status` cache is still
 * `'MAPPED'` but whose instance has already resolved to a terminal outcome
 * (published/failed/etc.) reflected in `instancesSummary`.
 *
 * `partial`/`missed`/`cancelled`/`publishing` (Stage 2c-i) are SELECTED
 * (never summed) straight off `instancesSummary` when present. There is no
 * raw-mapping fallback for these — `CalendarMapping.status` has no
 * PARTIAL/MISSED/CANCELLED value (see POST-STATUS-MODEL.md), so they're
 * `undefined` when the data source doesn't supply `instancesSummary`.
 */
export function getPostStatusCounts(post: PostStatusSignals): PostStatusCounts {
  const mappings = post.calendarMappings || [];
  const summary = post.instancesSummary;

  const mappingCounts = {
    failed: mappings.filter((m) => m.status === 'FAILED').length,
    published: mappings.filter((m) => m.status === 'PUBLISHED').length,
    scheduled: mappings.filter((m) => m.status === 'SCHEDULED').length,
    mapped: mappings.filter((m) => m.status === 'MAPPED').length,
  };

  return summary
    ? {
        failed: summary.failed,
        published: summary.published,
        scheduled: summary.scheduled,
        mapped: summary.mapped ?? mappingCounts.mapped,
        partial: summary.partial,
        missed: summary.missed,
        cancelled: summary.cancelled,
        publishing: summary.publishing,
      }
    : mappingCounts;
}

/**
 * Minimal set of status-signal fields needed to infer a POST VARIATION's
 * overall calendar status. Unlike `Post`, a variation has no top-level
 * `status` field — it resolves purely from `calendarMappings[].status`
 * counts, unioned with the variation-level `instancesSummary` (mirroring
 * `PostStatusSignals.instancesSummary`) once the backend supplies it.
 *
 * Same PARTIAL-detection caveat as `isPostFailed`: a partial variation (some
 * channels published, some failed) is persisted as
 * `CalendarMapping.status='PUBLISHED'`, never `'FAILED'`. Only
 * `instancesSummary.failed > 0` catches that case. A variation payload that
 * only carries `calendarMappings` (no `instancesSummary`) will therefore miss
 * partial failures — this degrades gracefully to the legacy raw-mapping-only
 * behavior rather than crashing.
 *
 * `partial`/`missed`/`cancelled`/`publishing` (Stage 2c-i) are the first-class
 * counterparts to the caveat above — SELECTED (never summed) straight off
 * `instancesSummary` when present, `undefined` otherwise (no raw-mapping
 * fallback exists for them; see POST-STATUS-MODEL.md).
 */
export interface VariationStatusSignals {
  calendarMappings?: { status: string }[];
  instancesSummary?: {
    mapped?: number;
    scheduled: number;
    published: number;
    failed: number;
    // First-class occurrence buckets (Stage 2c-i) — un-folded from `failed`.
    publishing?: number;
    partial?: number;
    missed?: number;
    cancelled?: number;
  } | null;
}

export interface VariationStatusCounts {
  mapped: number;
  scheduled: number;
  published: number;
  failed: number;
  // First-class occurrence buckets (Stage 2c-i) — select-not-sum from
  // `instancesSummary` when present; undefined when the data source doesn't
  // supply it (no reliable raw-mapping fallback exists for these states).
  partial?: number;
  missed?: number;
  cancelled?: number;
  publishing?: number;
}

/**
 * Aggregate calendar-mapping + instancesSummary counts for a post variation.
 *
 * `instancesSummary` (when the backend supplies it) is DERIVED server-side
 * from this SAME `calendarMappings` array that is also returned in the
 * payload — it is not an independent signal. Summing the raw mapping tally
 * with `instancesSummary` therefore double-counts every mapping (e.g. 2
 * published mappings would render as 4 published). FIX: prefer
 * `instancesSummary` when present (select, not sum), and only fall back to
 * the raw mapping tally when `instancesSummary` is absent (backend not yet
 * returning it, or a data source that never included it), matching the
 * variation badges' pre-#182 behavior.
 */
export function getVariationStatusCounts(
  variation: VariationStatusSignals
): VariationStatusCounts {
  const mappings = variation.calendarMappings || [];
  const summary = variation.instancesSummary;

  const mappingCounts = {
    failed: mappings.filter((m) => m.status === 'FAILED').length,
    published: mappings.filter((m) => m.status === 'PUBLISHED').length,
    scheduled: mappings.filter((m) => m.status === 'SCHEDULED').length,
    mapped: mappings.filter((m) => m.status === 'MAPPED').length,
  };

  return summary
    ? {
        failed: summary.failed,
        published: summary.published,
        scheduled: summary.scheduled,
        mapped: summary.mapped ?? mappingCounts.mapped,
        partial: summary.partial,
        missed: summary.missed,
        cancelled: summary.cancelled,
        publishing: summary.publishing,
      }
    : mappingCounts;
}

/**
 * Get the primary status for a variation.
 * (Priority: Failed > Published > Scheduled > Mapped > Unmapped)
 *
 * Mirrors `getPrimaryStatus`'s Failed-first ordering for posts: a genuinely
 * PARTIAL variation (some channels published, some failed) makes both the
 * `failed` and `published` counts non-zero, so Failed is checked FIRST to
 * resolve that tie in favor of surfacing the failure. Do NOT reorder.
 *
 * NOTE (Stage 2c-i): the return type now includes 'partial' | 'missed' |
 * 'cancelled' | 'publishing' to make the full occurrence vocabulary
 * available ahead of time. The collapse LOGIC below is UNCHANGED in this
 * step — see the equivalent note on `getPrimaryStatus`.
 */
export function getVariationPrimaryStatus(
  variation: VariationStatusSignals
): 'failed' | 'published' | 'scheduled' | 'mapped' | 'unmapped' | 'partial' | 'missed' | 'cancelled' | 'publishing' {
  const counts = getVariationStatusCounts(variation);
  // Mirrors `isPostFailed`'s needs-attention union at the post level: Partial
  // and Missed are folded into the 'failed' bucket here for the same reason
  // (the residual-fallback collapse logic is Failed-first — see the docstring
  // above and POST-STATUS-MODEL.md). Without this OR, a partial/missed-only
  // variation (no raw `FAILED` mapping) would fall through every branch below
  // and incorrectly resolve to 'unmapped' instead of needs-attention.
  if (counts.failed > 0 || (counts.partial ?? 0) > 0 || (counts.missed ?? 0) > 0) return 'failed';
  if (counts.published > 0) return 'published';
  if (counts.scheduled > 0) return 'scheduled';
  if (counts.mapped > 0) return 'mapped';
  return 'unmapped';
}
